import { Fragment, useContext } from "react";

import { useTranslation } from "react-i18next";

import CardItemValue from "components/TableDetails/components/CardItemValue";
import { TitleInfo } from "components/TableDetails/components/TitleInfo";
import { CARD_ITEMS, CARDS } from "components/TableDetails/DeviceDetails/types";
import { renderAvailableData } from "components/TableDetails/functions";
import TableContext from "context/table/tableContext";
import { isNotEmpty } from "shared/functions";

import { isFtPlatform } from "../../functions";
import { BLUETOOTH_PASSWORD_SOURCE, DetailsData } from "../../types";
import BluetoothPassword from "./BluetoothPassword";
import Description from "./Description";

const General = () => {
    const { detailsData } = useContext(TableContext);

    const {
        ble_dtb_version,
        ble_firmware,
        bootloader_version,
        bluetooth_password_source,
        company_name,
        current_configuration,
        current_firmware,
        dtb_version,
        gnss_version,
        group_name,
        model_platform,
        product_code,
        serial,
        spec_id
    }: DetailsData = detailsData;

    const { t } = useTranslation();

    const getFirmwareAndSpecIdValue = () => {
        const specIdValue =
            spec_id && spec_id > 1
                ? `(${t("Table##spec_id")}: ${spec_id})`
                : "";

        return `${current_firmware} ${specIdValue}`;
    };

    const list = [
        {
            title: CARD_ITEMS.Company,
            value: company_name,
            isAvailable: true
        },
        {
            title: CARD_ITEMS.Group,
            value: group_name,
            isAvailable: isNotEmpty(group_name)
        },
        {
            title: CARD_ITEMS.Description,
            customRender: <Description />,
            isAvailable: true
        },
        {
            title: CARD_ITEMS.Serial,
            value: String(serial),
            isAvailable: isNotEmpty(serial)
        },
        {
            title: CARD_ITEMS.ProductCode,
            value: product_code,
            isAvailable: isNotEmpty(product_code)
        },
        {
            title: CARD_ITEMS.Firmware,
            value: getFirmwareAndSpecIdValue(),
            isAvailable: isNotEmpty(current_firmware)
        },
        {
            title: CARD_ITEMS.Configuration,
            value: current_configuration,
            isAvailable:
                !isFtPlatform(model_platform) &&
                isNotEmpty(current_configuration)
        },
        {
            title: CARD_ITEMS.Dtb,
            value: dtb_version,
            isAvailable: isNotEmpty(dtb_version)
        },
        {
            title: CARD_ITEMS.BleFw,
            value: ble_firmware,
            isAvailable: isNotEmpty(ble_firmware)
        },
        {
            title: CARD_ITEMS.BleDtb,
            value: ble_dtb_version,
            isAvailable: isNotEmpty(ble_dtb_version)
        },
        {
            title: CARD_ITEMS.GnssVersion,
            value: gnss_version,
            isAvailable: isNotEmpty(gnss_version)
        },
        {
            title: CARD_ITEMS.Bootloader,
            value: bootloader_version,
            isAvailable: isNotEmpty(bootloader_version)
        },
        {
            title: CARD_ITEMS.BluetoothPassword,
            customRender: <BluetoothPassword />,
            isAvailable:
                bluetooth_password_source !== null &&
                bluetooth_password_source !== BLUETOOTH_PASSWORD_SOURCE.None
        }
    ];

    return (
        <div>
            <div>
                {t(`Table##${CARDS.General}`)}

                <TitleInfo
                    aria-label={t(`Table##${CARDS.General}`)}
                    cardName={CARDS.General}
                    list={list}
                />
            </div>

            <div>
                {renderAvailableData(list).map(
                    ({ title, customRender, value }) => {
                        return (
                            <Fragment key={title}>
                                {customRender || (
                                    <>
                                        <span>{t(`Table##${title}`)}:</span>
                                        <CardItemValue value={value} />
                                    </>
                                )}
                            </Fragment>
                        );
                    }
                )}
            </div>
        </div>
    );
};

export { General };
